import type {
	AccommodationStrategy,
	BlockSpanRef,
	BlockWithId,
	GridBlockPosition,
	GridLayoutData,
	GridRow,
	PendingBlockPosition,
	ValidationResult,
} from './grid-types';
import { rangesOverlap, validateNoOverlaps } from './grid-overlap';
import { GRID_CONFIG } from './grid-types';

export interface AccommodationContext {
	gridData: GridLayoutData;
	validatePosition: (
		rowIndex: number,
		colStart: number,
		colSpan: number,
		excludeChildId: string | null,
	) => ValidationResult;
	getRowBlocks: (rowIndex: number, excludeChildId?: string) => BlockWithId[];
}

export interface AccommodationOptions {
	blockPositionsBeforeDrag?: Record<string, GridBlockPosition>;
}

function getConflictingBlocks(
	positions: Record<string, GridBlockPosition>,
	childId: string,
	rowIndex: number,
	colStart: number,
	colSpan: number,
): BlockWithId[] {
	const blockEnd = colStart + colSpan;

	return Object.entries(positions)
		.filter(([id, pos]) => {
			if (id === childId || pos.rowIndex !== rowIndex) return false;
			return rangesOverlap(colStart, blockEnd, pos.colStart, pos.colStart + pos.colSpan);
		})
		.map(([id, pos]) => ({ id, pos }));
}

function shouldPreferShrinkAdjacent(
	childId: string,
	rowIndex: number,
	colStart: number,
	colSpan: number,
	options?: AccommodationOptions,
): boolean {
	const snapshotPositions = options?.blockPositionsBeforeDrag;

	if (!snapshotPositions) {
		return false;
	}

	const conflictingBlocks = getConflictingBlocks(snapshotPositions, childId, rowIndex, colStart, colSpan);

	return conflictingBlocks.some(({ pos }) => colStart > pos.colStart + pos.colSpan / 2);
}

/**
 * Try to shrink adjacent blocks to make room for a new block
 */
export function tryShrinkAdjacent(
	ctx: AccommodationContext,
	childId: string,
	rowIndex: number,
	colStart: number,
	colSpan: number,
): AccommodationStrategy {
	const rowBlocks = ctx.getRowBlocks(rowIndex, childId);
	const updatedPositions = { ...ctx.gridData.blockPositions };
	const blockEnd = colStart + colSpan;

	const conflictingBlocks = rowBlocks.filter(({ id, pos }) => {
		if (id === childId) return false;
		return rangesOverlap(colStart, blockEnd, pos.colStart, pos.colStart + pos.colSpan);
	});

	if (conflictingBlocks.length === 0) {
		return { canAccommodate: false };
	}

	for (const { id, pos } of conflictingBlocks) {
		const overlap = Math.min(blockEnd, pos.colStart + pos.colSpan) - Math.max(colStart, pos.colStart);
		const newSpan = pos.colSpan - overlap;

		if (newSpan < 1) {
			return { canAccommodate: false };
		}

		if (pos.colStart < colStart) {
			updatedPositions[id] = { ...pos, colSpan: newSpan };
		}
		else {
			updatedPositions[id] = { ...pos, colStart: blockEnd, colSpan: newSpan };
		}
	}

	updatedPositions[childId] = { rowIndex, colStart, colSpan };

	const finalValidation = validateNoOverlaps(updatedPositions, ctx.gridData.rows);

	if (!finalValidation.valid) {
		return { canAccommodate: false };
	}

	return {
		canAccommodate: true,
		strategy: 'shrink_adjacent',
		updatedPositions,
	};
}

/**
 * Try to push adjacent blocks to make room in the given direction.
 * Blocks keep their span if possible, but shrink minimally if they would exceed bounds.
 */
function tryPushInDirection(
	ctx: AccommodationContext,
	childId: string,
	rowIndex: number,
	colStart: number,
	colSpan: number,
	direction: 'left' | 'right',
): AccommodationStrategy {
	const rowBlocks = ctx.getRowBlocks(rowIndex, childId);
	const rowColumns = ctx.gridData.rows[rowIndex]?.columns ?? GRID_CONFIG.COLUMNS;
	const updatedPositions = { ...ctx.gridData.blockPositions };
	const blockEnd = colStart + colSpan;

	const blocksToMove = rowBlocks
		.filter(({ pos }) => {
			const otherEnd = pos.colStart + pos.colSpan;
			const overlaps = rangesOverlap(colStart, blockEnd, pos.colStart, otherEnd);

			if (direction === 'right') {
				return pos.colStart >= colStart || overlaps;
			}
			else {
				return otherEnd <= blockEnd && (otherEnd > colStart || overlaps);
			}
		})
		.toSorted((a, b) => direction === 'right' ? a.pos.colStart - b.pos.colStart : b.pos.colStart - a.pos.colStart);

	if (blocksToMove.length === 0) {
		return { canAccommodate: false };
	}

	const [firstConflicting] = blocksToMove;
	const pushAmount = direction === 'right'
		? blockEnd - firstConflicting!.pos.colStart
		: firstConflicting!.pos.colStart + firstConflicting!.pos.colSpan - colStart;

	if (pushAmount <= 0) {
		return { canAccommodate: false };
	}

	let needsShrink = false;
	const newPositions: PendingBlockPosition[] = [];

	for (const { id, pos } of blocksToMove) {
		let newColStart = direction === 'right' ? pos.colStart + pushAmount : pos.colStart - pushAmount;
		let newSpan = pos.colSpan;

		if (direction === 'right') {
			if (newColStart + newSpan > rowColumns) {
				const overflow = newColStart + newSpan - rowColumns;
				newSpan = pos.colSpan - overflow;

				if (newSpan < 1) return { canAccommodate: false };
				needsShrink = true;
			}

			if (newColStart < 0) return { canAccommodate: false };
		}
		else {
			if (newColStart < 0) {
				const overflow = -newColStart;
				newSpan = pos.colSpan - overflow;
				newColStart = 0;

				if (newSpan < 1) return { canAccommodate: false };
				needsShrink = true;
			}
		}

		newPositions.push({ id, newColStart, newSpan });
	}

	for (const { id, newColStart, newSpan } of newPositions) {
		const existingPos = ctx.gridData.blockPositions[id]!;
		updatedPositions[id] = { ...existingPos, colStart: newColStart, colSpan: newSpan };
	}

	updatedPositions[childId] = { rowIndex, colStart, colSpan };

	const finalValidation = validateNoOverlaps(updatedPositions, ctx.gridData.rows);

	if (!finalValidation.valid) {
		return { canAccommodate: false };
	}

	return {
		canAccommodate: true,
		strategy: needsShrink ? 'push_and_shrink' : 'push_adjacent',
		updatedPositions,
	};
}

/**
 * Try to push adjacent blocks to make room without resizing.
 * Tries pushing right first, then left.
 */
export function tryPushAdjacent(
	ctx: AccommodationContext,
	childId: string,
	rowIndex: number,
	colStart: number,
	colSpan: number,
): AccommodationStrategy {
	const pushRightResult = tryPushInDirection(ctx, childId, rowIndex, colStart, colSpan, 'right');

	if (pushRightResult.canAccommodate) {
		return pushRightResult;
	}

	const pushLeftResult = tryPushInDirection(ctx, childId, rowIndex, colStart, colSpan, 'left');

	if (pushLeftResult.canAccommodate) {
		return pushLeftResult;
	}

	return { canAccommodate: false };
}

/**
 * Categorize blocks into left and right groups based on drop position
 */
function categorizeBlocks(
	currentBlocks: BlockWithId[],
	colStart: number,
	colSpan: number,
): { blocksLeft: BlockSpanRef[]; blocksRight: BlockSpanRef[] } {
	const blocksLeft: BlockSpanRef[] = [];
	const blocksRight: BlockSpanRef[] = [];
	const dropCenter = colStart + colSpan / 2;

	// Sort by colStart first so each bucket is already in order
	const sorted = currentBlocks.toSorted((a, b) => a.pos.colStart - b.pos.colStart);

	for (const { id, pos } of sorted) {
		const blockCenter = pos.colStart + pos.colSpan / 2;

		if (blockCenter < dropCenter) {
			blocksLeft.push({ id, span: pos.colSpan });
		}
		else {
			blocksRight.push({ id, span: pos.colSpan });
		}
	}

	return { blocksLeft, blocksRight };
}

/**
 * Calculate proportionally distributed spans for blocks on one side of the dropped block.
 * Always fills all available columns to avoid gaps.
 */
function calculateSideSpans(
	blocks: BlockSpanRef[],
	availableColumns: number,
): number[] {
	if (blocks.length === 0) return [];

	const totalSpan = blocks.reduce((sum, b) => sum + b.span, 0);

	const newSpans: number[] = [];
	let remainingColumns = availableColumns;
	let remainingBlocks = blocks.length;

	for (const [i, block] of blocks.entries()) {
		let newSpan: number;

		if (i === blocks.length - 1) {
			// Last block takes all remaining columns to fill the space
			newSpan = Math.max(1, remainingColumns);
		}
		else {
			const proportion = block.span / totalSpan;
			newSpan = Math.max(1, Math.round(availableColumns * proportion));
			const minForRemaining = remainingBlocks - 1;
			newSpan = Math.min(newSpan, remainingColumns - minForRemaining);
		}

		newSpans.push(newSpan);
		remainingColumns -= newSpan;
		remainingBlocks--;
	}

	return newSpans;
}

/**
 * Apply positions to blocks with the dropped block at a fixed position.
 * The dropped block's colStart is ALWAYS preserved - other blocks are arranged around it.
 */
function applyPositionsWithFixedDrop(
	blocksLeft: BlockSpanRef[],
	blocksRight: BlockSpanRef[],
	childId: string,
	rowIndex: number,
	colStart: number,
	colSpan: number,
	existingPositions: Record<string, GridBlockPosition>,
	rowColumns: number,
): Record<string, GridBlockPosition> {
	const updatedPositions = { ...existingPositions };

	// Calculate available space on each side
	const availableLeft = colStart;
	const availableRight = rowColumns - (colStart + colSpan);

	// Calculate spans for left blocks
	const leftSpans = calculateSideSpans(blocksLeft, availableLeft);

	// Calculate spans for right blocks
	const rightSpans = calculateSideSpans(blocksRight, availableRight);

	// Place left blocks (from left to right, ending at colStart)
	let currentCol = 0;

	for (const [i, block] of blocksLeft.entries()) {
		const existingPos = existingPositions[block.id]!;

		updatedPositions[block.id] = {
			...existingPos,
			colStart: currentCol,
			colSpan: leftSpans[i]!,
		};

		currentCol += leftSpans[i]!;
	}

	// Place the dropped block at the EXACT requested position
	updatedPositions[childId] = {
		rowIndex,
		colStart,
		colSpan,
	};

	// Place right blocks (from colStart + colSpan to end)
	currentCol = colStart + colSpan;

	for (const [i, block] of blocksRight.entries()) {
		const existingPos = existingPositions[block.id]!;

		updatedPositions[block.id] = {
			...existingPos,
			colStart: currentCol,
			colSpan: rightSpans[i]!,
		};

		currentCol += rightSpans[i]!;
	}

	return updatedPositions;
}

/**
 * Try to push blocks and resize them if total span exceeds grid width.
 * The dropped block's colStart is ALWAYS preserved - other blocks are shrunk/moved to fit.
 */
export function tryPushWithResize(
	ctx: AccommodationContext,
	childId: string,
	rowIndex: number,
	colStart: number,
	colSpan: number,
): AccommodationStrategy {
	const rowBlocks = ctx.getRowBlocks(rowIndex, childId);
	const rowColumns = ctx.gridData.rows[rowIndex]?.columns ?? GRID_CONFIG.COLUMNS;
	const currentBlocks = rowBlocks.filter(({ id }) => id !== childId);

	const totalBlocksAfterDrop = currentBlocks.length + 1;

	if (totalBlocksAfterDrop > rowColumns) {
		return { canAccommodate: false };
	}

	// Check if the target position is valid
	if (colStart < 0 || colStart + colSpan > rowColumns) {
		return { canAccommodate: false };
	}

	const { blocksLeft, blocksRight } = categorizeBlocks(currentBlocks, colStart, colSpan);

	// Check if we have enough space for minimum 1-column per block on each side
	const availableLeft = colStart;
	const availableRight = rowColumns - (colStart + colSpan);

	if (blocksLeft.length > availableLeft || blocksRight.length > availableRight) {
		return { canAccommodate: false };
	}

	// Apply positions with the dropped block at the EXACT target position
	const updatedPositions = applyPositionsWithFixedDrop(
		blocksLeft,
		blocksRight,
		childId,
		rowIndex,
		colStart,
		colSpan,
		ctx.gridData.blockPositions,
		rowColumns,
	);

	const finalValidation = validateNoOverlaps(updatedPositions, ctx.gridData.rows);

	if (!finalValidation.valid) {
		return { canAccommodate: false };
	}

	return {
		canAccommodate: true,
		strategy: 'shrink_all',
		updatedPositions,
	};
}

/**
 * Try creating a new row for the block
 */
export function tryAccommodateNewRow(
	ctx: AccommodationContext,
	colStart: number,
	colSpan: number,
): AccommodationStrategy {
	if (colStart < 0 || colStart + colSpan > GRID_CONFIG.COLUMNS || colSpan < 1) {
		return { canAccommodate: false };
	}

	const updatedRows: GridRow[] = [...ctx.gridData.rows, { columns: GRID_CONFIG.COLUMNS }];

	return {
		canAccommodate: true,
		strategy: 'create_row',
		updatedRows,
	};
}

/**
 * Main accommodation function that tries various strategies in order:
 * 1. fits - position is already valid
 * 2. create_row - if row index is out of bounds
 * 3. push_adjacent - push blocks without shrinking
 * 4. shrink_adjacent - shrink only overlapping blocks
 * 5. shrink_all (via tryPushWithResize) - redistribute all blocks proportionally
 */
export function tryAccommodate(
	ctx: AccommodationContext,
	childId: string,
	rowIndex: number,
	colStart: number,
	colSpan: number,
	options?: AccommodationOptions,
): AccommodationStrategy {
	const validation = ctx.validatePosition(rowIndex, colStart, colSpan, childId);

	if (validation.valid) {
		return { canAccommodate: true, strategy: 'fits' };
	}

	if (rowIndex < 0 || rowIndex >= ctx.gridData.rows.length) {
		return tryAccommodateNewRow(ctx, colStart, colSpan);
	}

	const preferShrinkAdjacent = shouldPreferShrinkAdjacent(childId, rowIndex, colStart, colSpan, options);

	if (preferShrinkAdjacent) {
		const shrinkFirstResult = tryShrinkAdjacent(ctx, childId, rowIndex, colStart, colSpan);

		if (shrinkFirstResult.canAccommodate) {
			return shrinkFirstResult;
		}
	}

	// Try pushing blocks without shrinking first
	const pushResult = tryPushAdjacent(ctx, childId, rowIndex, colStart, colSpan);

	if (pushResult.canAccommodate) {
		return pushResult;
	}

	// Try shrinking only conflicting blocks
	const shrinkResult = tryShrinkAdjacent(ctx, childId, rowIndex, colStart, colSpan);

	if (shrinkResult.canAccommodate) {
		return shrinkResult;
	}

	// Try redistributing all blocks proportionally (shrink_all strategy)
	const pushResizeResult = tryPushWithResize(ctx, childId, rowIndex, colStart, colSpan);

	if (pushResizeResult.canAccommodate) {
		return pushResizeResult;
	}

	return { canAccommodate: false };
}
